import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
  Switch,
  FormControlLabel,
  Button,
  Badge,
  Menu,
  MenuItem as MuiMenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Assessment,
  AccessTime,
  People,
  Settings,
  Web,
  Code,
  Menu as MenuIconItem,
  Chat,
  Brightness4,
  Brightness7,
  Logout,
  Notifications,
  PlayArrow,
  Stop,
  Pause,
  PlayCircleOutline,
  Add,
  OnlinePrediction,
  Report,
  Work,
  AdminPanelSettings,
  History
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { useSettings } from '../contexts/SettingsContext';
import { useWorkTime } from '../contexts/WorkTimeContext';
import { getFilteredMenuItems } from '../utils/menuConfig';
import { MenuItem, WorkTimeStatus } from '../types';

// drawerWidth artık settings'den gelecek

const iconMap: { [key: string]: React.ComponentType } = {
  Dashboard,
  Assessment,
  AccessTime,
  People,
  Settings,
  Web,
  Code,
  Menu: MenuIconItem,
  Chat
};

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const { mode, toggleMode } = useCustomTheme();
  const { settings } = useSettings();
  const { workTimeStatus, startWork, endWork, startBreak, endBreak, isLoading: workTimeLoading } = useWorkTime();
  const navigate = useNavigate();
  const location = useLocation();

  const [notifAnchorEl, setNotifAnchorEl] = useState<null | HTMLElement>(null);
  const [workTimeAnchorEl, setWorkTimeAnchorEl] = useState<null | HTMLElement>(null);
  const [fabAnchorEl, setFabAnchorEl] = useState<null | HTMLElement>(null);
  const [onlineUsersDialogOpen, setOnlineUsersDialogOpen] = useState(false);
  
  // Frontend mock: Rapor ve Chat bildirimleri
  const notificationsData: Array<{
    id: string;
    title: string;
    time: string;
    read: boolean;
    type: 'report' | 'chat';
    route: string;
  }> = [
    { id: 'r1', title: 'Aylık raporunuz hazır', time: '5 dk önce', read: false, type: 'report', route: '/reports/monthly' },
    { id: 'c1', title: 'Canlı Chat: Yeni mesaj', time: '12 dk önce', read: false, type: 'chat', route: '/chat' },
    { id: 'r2', title: 'Günlük rapor onaylandı', time: '1 saat önce', read: false, type: 'report', route: '/reports/daily' },
    { id: 'c2', title: 'Satış grubu: 3 yeni mesaj', time: 'Dün', read: true, type: 'chat', route: '/chat' }
  ];
  const unreadCount = notificationsData.filter(n => !n.read).length;
  const openNotifMenu = (e: React.MouseEvent<HTMLElement>) => setNotifAnchorEl(e.currentTarget);
  const closeNotifMenu = () => setNotifAnchorEl(null);
  const handleClickNotification = (route: string) => {
    closeNotifMenu();
    navigate(route);
  };

  // Mesai takip fonksiyonları
  const openWorkTimeMenu = (e: React.MouseEvent<HTMLElement>) => setWorkTimeAnchorEl(e.currentTarget);
  const closeWorkTimeMenu = () => setWorkTimeAnchorEl(null);

  const handleWorkStart = async () => {
    const success = await startWork();
    if (success) {
      closeWorkTimeMenu();
    }
  };

  const handleWorkEnd = async () => {
    const success = await endWork();
    if (success) {
      closeWorkTimeMenu();
    }
  };

  const handleBreakStart = async () => {
    const success = await startBreak();
    if (success) {
      closeWorkTimeMenu();
    }
  };

  const handleBreakEnd = async () => {
    const success = await endBreak();
    if (success) {
      closeWorkTimeMenu();
    }
  };

  // FAB menü fonksiyonları
  const openFabMenu = (e: React.MouseEvent<HTMLElement>) => setFabAnchorEl(e.currentTarget);
  const closeFabMenu = () => setFabAnchorEl(null);

  const handleFabMenuClick = (route: string) => {
    if (route === '/users') {
      setOnlineUsersDialogOpen(true);
      closeFabMenu();
    } else {
      navigate(route);
      closeFabMenu();
    }
  };

  // Mock online kullanıcılar
  const onlineUsers = [
    { id: '1', name: 'Admin User', role: 'Admin', status: 'online', lastSeen: 'Şimdi' },
    { id: '2', name: 'Developer User', role: 'Developer', status: 'online', lastSeen: '2 dk önce' },
    { id: '3', name: 'Call Admin', role: 'Call Admin', status: 'online', lastSeen: '5 dk önce' },
    { id: '4', name: 'LiveChat Admin', role: 'LiveChat Admin', status: 'online', lastSeen: '1 dk önce' },
    { id: '5', name: 'Risk Admin', role: 'Risk Admin', status: 'online', lastSeen: '3 dk önce' },
    { id: '6', name: 'Call User', role: 'Call', status: 'online', lastSeen: 'Şimdi' },
    { id: '7', name: 'LiveChat User', role: 'LiveChat', status: 'online', lastSeen: '4 dk önce' },
    { id: '8', name: 'Risk User', role: 'Risk', status: 'online', lastSeen: '2 dk önce' }
  ];

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'Admin': return 'error';
      case 'Developer': return 'secondary';
      case 'Call Admin': return 'primary';
      case 'LiveChat Admin': return 'success';
      case 'Risk Admin': return 'warning';
      case 'Call': return 'info';
      case 'LiveChat': return 'success';
      case 'Risk': return 'warning';
      default: return 'default';
    }
  };

  const filteredMenuItems = user ? getFilteredMenuItems(user.role) : [];
  const drawerWidth = settings.sidebarWidth;
  
  // Panel sayfasında sidebar'ı gizle
  const isPanelPage = location.pathname === '/panel';

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleItemClick = (item: MenuItem) => {
    navigate(item.path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const isItemSelected = (item: MenuItem): boolean => {
    return location.pathname === item.path;
  };

  const renderMenuItem = (item: MenuItem) => {
    const IconComponent = iconMap[item.icon] || Dashboard;
    const isSelected = isItemSelected(item);

    return (
      <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
        <ListItemButton
          onClick={() => handleItemClick(item)}
          selected={isSelected}
          sx={{
            borderRadius: 2,
            mx: 1,
            '&.Mui-selected': {
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
              '& .MuiListItemIcon-root': {
                color: 'white',
              },
            },
            '&:hover': {
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
              transform: 'translateX(4px)',
              transition: 'all 0.2s ease',
            },
          }}
        >
          <ListItemIcon sx={{ 
            color: isSelected ? 'white' : 'text.primary',
            minWidth: 40
          }}>
            <IconComponent />
          </ListItemIcon>
          <ListItemText 
            primary={item.title} 
            primaryTypographyProps={{
              fontWeight: isSelected ? 'bold' : 'medium',
              fontSize: '0.9rem'
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box>
                <Toolbar sx={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  height: settings.headerHeight
                }}>
                  <Typography variant="h6" noWrap component="div" fontWeight="bold">
                    📊 {settings.siteName}
                  </Typography>
                </Toolbar>
        <Divider />
        
        {/* User Profile Card */}
        <Box sx={{ p: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              width: 50, 
              height: 50, 
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.2rem',
              fontWeight: 'bold',
              color: 'white'
            }}>
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </Box>
            <Box>
              <Typography variant="body1" fontWeight="bold" color="white">
                {user?.firstName} {user?.lastName}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {user?.role.toUpperCase()}
              </Typography>
            </Box>
          </Box>
        </Box>
        
        <Divider />
        <List sx={{ px: 1, py: 2 }}>
          {filteredMenuItems.map(item => renderMenuItem(item))}
        </List>
      </Box>
      
      {/* Alt kısım - Tema değiştirme ve çıkış */}
      <Box sx={{ mt: 'auto', p: 2 }}>
        <Divider sx={{ mb: 2 }} />
        
        {/* Tema değiştirme */}
        <FormControlLabel
          control={
            <Switch
              checked={mode === 'dark'}
              onChange={toggleMode}
              icon={<Brightness7 />}
              checkedIcon={<Brightness4 />}
            />
          }
          label={mode === 'dark' ? 'Koyu Mod' : 'Açık Mod'}
          sx={{ mb: 2, width: '100%' }}
        />
        
        {/* Çıkış butonu */}
        <Button
          fullWidth
          variant="outlined"
          startIcon={<Logout />}
          onClick={logout}
          sx={{ 
            color: 'error.main',
            borderColor: 'error.main',
            '&:hover': {
              backgroundColor: 'error.main',
              color: 'white'
            }
          }}
        >
          Çıkış Yap
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: 'none',
          height: settings.headerHeight
        }}
      >
        <Toolbar sx={{ height: settings.headerHeight }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" fontWeight="bold">
            {settings.siteName}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton color="inherit" onClick={openNotifMenu} aria-label="notifications">
            <Badge color="error" badgeContent={unreadCount} overlap="circular">
              <Notifications />
            </Badge>
          </IconButton>
          
          {/* Hızlı İşlemler - Mesai Takip */}
          <IconButton 
            color="inherit" 
            onClick={openWorkTimeMenu} 
            aria-label="work time"
            disabled={workTimeLoading}
            sx={{ 
              ml: 1,
              backgroundColor: workTimeStatus === WorkTimeStatus.WORKING ? 'rgba(76, 175, 80, 0.2)' : 
                              workTimeStatus === WorkTimeStatus.ON_BREAK ? 'rgba(255, 152, 0, 0.2)' : 
                              'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                backgroundColor: workTimeStatus === WorkTimeStatus.WORKING ? 'rgba(76, 175, 80, 0.3)' : 
                               workTimeStatus === WorkTimeStatus.ON_BREAK ? 'rgba(255, 152, 0, 0.3)' : 
                               'rgba(255, 255, 255, 0.2)'
              }
            }}
          >
            {workTimeStatus === WorkTimeStatus.WORKING ? <PlayArrow /> :
             workTimeStatus === WorkTimeStatus.ON_BREAK ? <Pause /> :
             <Stop />}
          </IconButton>
          <Menu
            anchorEl={notifAnchorEl}
            open={Boolean(notifAnchorEl)}
            onClose={closeNotifMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {notificationsData.length === 0 && (
              <MuiMenuItem disabled>Bildirim yok</MuiMenuItem>
            )}
            {/* Rapor bildirimleri */}
            {notificationsData.some(n => n.type === 'report') && (
              <MuiMenuItem disabled>Raporlar</MuiMenuItem>
            )}
            {notificationsData
              .filter(n => n.type === 'report')
              .map(item => (
                <MuiMenuItem key={item.id} onClick={() => handleClickNotification(item.route)} sx={{ whiteSpace: 'normal', alignItems: 'start' }}>
                  <ListItemText
                    primary={item.title}
                    secondary={item.time}
                    primaryTypographyProps={{ fontWeight: item.read ? 'regular' : 'bold' }}
                  />
                </MuiMenuItem>
              ))}
            {/* Chat bildirimleri */}
            {notificationsData.some(n => n.type === 'chat') && (
              <MuiMenuItem disabled>Canlı Chat</MuiMenuItem>
            )}
            {notificationsData
              .filter(n => n.type === 'chat')
              .map(item => (
                <MuiMenuItem key={item.id} onClick={() => handleClickNotification(item.route)} sx={{ whiteSpace: 'normal', alignItems: 'start' }}>
                  <ListItemText
                    primary={item.title}
                    secondary={item.time}
                    primaryTypographyProps={{ fontWeight: item.read ? 'regular' : 'bold' }}
                  />
                </MuiMenuItem>
              ))}
          </Menu>
          
          {/* Mesai Takip Menüsü */}
          <Menu
            anchorEl={workTimeAnchorEl}
            open={Boolean(workTimeAnchorEl)}
            onClose={closeWorkTimeMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MuiMenuItem disabled>
              <Typography variant="subtitle2" fontWeight="bold">
                Mesai Takip
              </Typography>
            </MuiMenuItem>
            
            {workTimeStatus === WorkTimeStatus.OFF_DUTY && (
              <MuiMenuItem onClick={handleWorkStart}>
                <ListItemIcon>
                  <PlayArrow color="success" />
                </ListItemIcon>
                <ListItemText primary="Mesai Başlat" />
              </MuiMenuItem>
            )}
            
            {workTimeStatus === WorkTimeStatus.WORKING && (
              <>
                <MuiMenuItem onClick={handleBreakStart}>
                  <ListItemIcon>
                    <Pause color="warning" />
                  </ListItemIcon>
                  <ListItemText primary="Mola Başlat" />
                </MuiMenuItem>
                <MuiMenuItem onClick={handleWorkEnd}>
                  <ListItemIcon>
                    <Stop color="error" />
                  </ListItemIcon>
                  <ListItemText primary="Mesai Bitir" />
                </MuiMenuItem>
              </>
            )}
            
            {workTimeStatus === WorkTimeStatus.ON_BREAK && (
              <MuiMenuItem onClick={handleBreakEnd}>
                <ListItemIcon>
                  <PlayCircleOutline color="success" />
                </ListItemIcon>
                <ListItemText primary="Mola Bitir" />
              </MuiMenuItem>
            )}
            
            <MuiMenuItem onClick={() => { navigate('/work-time'); closeWorkTimeMenu(); }}>
              <ListItemIcon>
                <AccessTime />
              </ListItemIcon>
              <ListItemText primary="Mesai Detayları" />
            </MuiMenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{ 
          flexGrow: 1, 
          p: isPanelPage ? 0 : settings.contentPadding, 
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: `${settings.headerHeight}px`,
          backgroundColor: isPanelPage ? 'transparent' : (mode === 'dark' ? settings.darkModeBackground : settings.lightModeBackground),
          color: mode === 'dark' ? settings.darkModeTextColor : settings.lightModeTextColor,
          minHeight: '100vh'
        }}
      >
        {children}
      </Box>

      {/* Global Floating Action Button - Tüm Sayfalarda (Panel hariç) */}
      {!isPanelPage && (
      <>
      <Box
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1000
        }}
      >
        <IconButton
          sx={{
            width: 56,
            height: 56,
            backgroundColor: 'primary.main',
            color: 'white',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            '&:hover': {
              backgroundColor: 'primary.dark',
              transform: 'scale(1.05)',
              boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
            },
            transition: 'all 0.2s ease-in-out'
          }}
          onClick={openFabMenu}
          aria-label="Hızlı İşlemler"
        >
          <Add sx={{ fontSize: 28 }} />
        </IconButton>
      </Box>

      {/* FAB Menüsü - Rol Bazlı */}
      <Menu
        anchorEl={fabAnchorEl}
        open={Boolean(fabAnchorEl)}
        onClose={closeFabMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{ mt: -1 }}
      >
        <MuiMenuItem disabled>
          <Typography variant="subtitle2" fontWeight="bold">
            Hızlı İşlemler
          </Typography>
        </MuiMenuItem>

        {/* Admin Menüsü */}
        {user?.role === 'admin' && [
          <MuiMenuItem key="admin-online" onClick={() => handleFabMenuClick('/users')}>
            <ListItemIcon>
              <OnlinePrediction color="success" />
            </ListItemIcon>
            <ListItemText primary="Online Kullanıcılar" />
          </MuiMenuItem>,
          <MuiMenuItem key="admin-report" onClick={() => handleFabMenuClick('/reports/create')}>
            <ListItemIcon>
              <Report color="primary" />
            </ListItemIcon>
            <ListItemText primary="Rapor Oluştur" />
          </MuiMenuItem>,
          <MuiMenuItem key="admin-report-settings" onClick={() => handleFabMenuClick('/settings/reports')}>
            <ListItemIcon>
              <Settings color="info" />
            </ListItemIcon>
            <ListItemText primary="Rapor Ayarları" />
          </MuiMenuItem>,
          <MuiMenuItem key="admin-page-settings" onClick={() => handleFabMenuClick('/settings/pages')}>
            <ListItemIcon>
              <AdminPanelSettings color="warning" />
            </ListItemIcon>
            <ListItemText primary="Sayfa Ayarları" />
          </MuiMenuItem>,
          <MuiMenuItem key="admin-users" onClick={() => handleFabMenuClick('/users')}>
            <ListItemIcon>
              <People color="info" />
            </ListItemIcon>
            <ListItemText primary="Kullanıcı Yönetimi" />
          </MuiMenuItem>,
          <MuiMenuItem key="admin-work-time" onClick={() => handleFabMenuClick('/work-time')}>
            <ListItemIcon>
              <Work color="secondary" />
            </ListItemIcon>
            <ListItemText primary="Mesai Takip" />
          </MuiMenuItem>,
          <MuiMenuItem key="admin-logs" onClick={() => handleFabMenuClick('/logs')}>
            <ListItemIcon>
              <History color="info" />
            </ListItemIcon>
            <ListItemText primary="Loglar" />
          </MuiMenuItem>
        ]}

        {/* LiveChat Admin Menüsü */}
        {user?.role === 'livechat_admin' && [
          <MuiMenuItem key="livechat-online" onClick={() => handleFabMenuClick('/chat')}>
            <ListItemIcon>
              <OnlinePrediction color="success" />
            </ListItemIcon>
            <ListItemText primary="Canlı Chat Online Kullanıcılar" />
          </MuiMenuItem>,
          <MuiMenuItem key="livechat-report" onClick={() => handleFabMenuClick('/reports/create')}>
            <ListItemIcon>
              <Report color="primary" />
            </ListItemIcon>
            <ListItemText primary="Rapor Oluştur" />
          </MuiMenuItem>
        ]}

        {/* Risk Admin Menüsü */}
        {user?.role === 'risk_admin' && [
          <MuiMenuItem key="risk-online" onClick={() => handleFabMenuClick('/users')}>
            <ListItemIcon>
              <OnlinePrediction color="success" />
            </ListItemIcon>
            <ListItemText primary="Risk Online Kullanıcılar" />
          </MuiMenuItem>,
          <MuiMenuItem key="risk-report" onClick={() => handleFabMenuClick('/reports/create')}>
            <ListItemIcon>
              <Report color="primary" />
            </ListItemIcon>
            <ListItemText primary="Rapor Oluştur" />
          </MuiMenuItem>
        ]}

        {/* Call Admin Menüsü */}
        {user?.role === 'call_admin' && [
          <MuiMenuItem key="call-online" onClick={() => handleFabMenuClick('/users')}>
            <ListItemIcon>
              <OnlinePrediction color="success" />
            </ListItemIcon>
            <ListItemText primary="Call Online Kullanıcılar" />
          </MuiMenuItem>,
          <MuiMenuItem key="call-report" onClick={() => handleFabMenuClick('/reports/create')}>
            <ListItemIcon>
              <Report color="primary" />
            </ListItemIcon>
            <ListItemText primary="Rapor Oluştur" />
          </MuiMenuItem>
        ]}

        {/* Diğer Roller için Varsayılan Menü */}
        {!['admin', 'livechat_admin', 'risk_admin', 'call_admin'].includes(user?.role || '') && (
          <MuiMenuItem onClick={() => handleFabMenuClick('/reports/create')}>
            <ListItemIcon>
              <Report color="primary" />
            </ListItemIcon>
            <ListItemText primary="Rapor Oluştur" />
          </MuiMenuItem>
        )}
      </Menu>
      </>
      )}

      {/* Online Kullanıcılar Dialog */}
      <Dialog 
        open={onlineUsersDialogOpen} 
        onClose={() => setOnlineUsersDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <OnlinePrediction color="success" />
            <Typography variant="h6">
              Online Kullanıcılar ({onlineUsers.length})
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <List>
            {onlineUsers.map((user, index) => (
              <React.Fragment key={user.id}>
                <ListItem>
                  <ListItemIcon>
                    <Avatar sx={{ 
                      bgcolor: getRoleColor(user.role) + '.main',
                      width: 40,
                      height: 40,
                      fontSize: '1rem'
                    }}>
                      {user.name.split(' ').map(n => n[0]).join('')}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {user.name}
                        </Typography>
                        <Chip 
                          label={user.role} 
                          size="small" 
                          color={getRoleColor(user.role) as any}
                          variant="outlined"
                        />
                        <Chip 
                          label="Online" 
                          size="small" 
                          color="success"
                          variant="filled"
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        Son görülme: {user.lastSeen}
                      </Typography>
                    }
                  />
                </ListItem>
                {index < onlineUsers.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOnlineUsersDialogOpen(false)}>
            Kapat
          </Button>
          <Button 
            variant="contained" 
            onClick={() => {
              setOnlineUsersDialogOpen(false);
              navigate('/users');
            }}
          >
            Tüm Kullanıcıları Görüntüle
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Layout;